import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PWAInstallProvider } from '@/contexts/PWAInstallContext';
import { Toaster } from 'sonner';

import SplashScreen from '@/pages/SplashScreen';
import WelcomePage from '@/pages/WelcomePage';
import LoginPage from '@/pages/LoginPage';

import AdminDashboard from '@/pages/admin/AdminDashboard';
import ManajemenSantri from '@/pages/admin/ManajemenSantri';
import ManajemenGuru from '@/pages/admin/ManajemenGuru';
import ManajemenHalaqah from '@/pages/admin/ManajemenHalaqah';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminBerita from '@/pages/admin/AdminBerita';
import AdminToko from '@/pages/admin/AdminToko';
import ManajemenAkun from '@/pages/admin/ManajemenAkun';
import AdminProgramDonasi from '@/pages/admin/AdminProgramDonasi';

import GuruDashboard from '@/pages/guru/GuruDashboard';
import InputSetoran from '@/pages/guru/InputSetoran';
import InputTasmi from '@/pages/guru/InputTasmi';
import RekapAbsensi from '@/pages/guru/RekapAbsensi';
import GuruRiwayat from '@/pages/guru/GuruRiwayat';

import SantriDashboard from '@/pages/santri/SantriDashboard';
import SantriProfile from '@/pages/santri/SantriProfile';
import TasmiPage from '@/pages/santri/TasmiPage';
import GuruProfile from '@/pages/guru/GuruProfile';
import ChatPage from '@/pages/ChatPage';
import ChatbotPage from '@/pages/ChatbotPage';
import GuruUjianTahfidz from '@/pages/guru/GuruUjianTahfidz';
import SantriUjianTahfidz from '@/pages/santri/SantriUjianTahfidz';
import PedomanPage from '@/pages/PedomanPage';

import TasbihPage from '@/pages/TasbihPage';
import BeritaPage from '@/pages/BeritaPage';
import AlQuranPage from '@/pages/AlQuranPage';
import JadwalSholatPage from '@/pages/JadwalSholatPage';
import TokoPage from '@/pages/TokoPage';
import TokoProdukDetail from '@/pages/TokoProdukDetail';
import DonasiPage from '@/pages/DonasiPage';

function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'guru') return <Navigate to="/guru" replace />;
  if (user.role === 'santri') return <Navigate to="/santri" replace />;
  return <Navigate to="/login" replace />;
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <Routes>
      <Route path="/" element={<SplashScreen />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/santri" element={<ProtectedRoute roles={['admin']}><ManajemenSantri /></ProtectedRoute>} />
      <Route path="/admin/guru" element={<ProtectedRoute roles={['admin']}><ManajemenGuru /></ProtectedRoute>} />
      <Route path="/admin/halaqah" element={<ProtectedRoute roles={['admin']}><ManajemenHalaqah /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={['admin']}><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/berita" element={<ProtectedRoute roles={['admin']}><AdminBerita /></ProtectedRoute>} />
      <Route path="/admin/toko" element={<ProtectedRoute roles={['admin']}><AdminToko /></ProtectedRoute>} />
      <Route path="/admin/akun" element={<ProtectedRoute roles={['admin']}><ManajemenAkun /></ProtectedRoute>} />
      <Route path="/admin/donasi" element={<ProtectedRoute roles={['admin']}><AdminProgramDonasi /></ProtectedRoute>} />

      {/* Guru routes */}
      <Route path="/guru" element={<ProtectedRoute roles={['guru', 'admin']}><GuruDashboard /></ProtectedRoute>} />
      <Route path="/guru/setoran" element={<ProtectedRoute roles={['guru', 'admin']}><InputSetoran /></ProtectedRoute>} />
      <Route path="/guru/tasmi" element={<ProtectedRoute roles={['guru', 'admin']}><InputTasmi /></ProtectedRoute>} />
      <Route path="/guru/rekap" element={<ProtectedRoute roles={['guru', 'admin']}><RekapAbsensi /></ProtectedRoute>} />
      <Route path="/guru/ujian-tahfidz" element={<ProtectedRoute roles={['guru', 'admin']}><GuruUjianTahfidz /></ProtectedRoute>} />
      <Route path="/guru/riwayat" element={<ProtectedRoute roles={['guru', 'admin']}><GuruRiwayat /></ProtectedRoute>} />

      {/* Santri routes */}
      <Route path="/santri" element={<ProtectedRoute roles={['santri']}><SantriDashboard /></ProtectedRoute>} />
      <Route path="/santri/setoran" element={<ProtectedRoute roles={['santri']}><SantriDashboard /></ProtectedRoute>} />
      <Route path="/santri/progress" element={<ProtectedRoute roles={['santri']}><SantriDashboard /></ProtectedRoute>} />
      <Route path="/santri/tasmi" element={<ProtectedRoute roles={['santri']}><TasmiPage /></ProtectedRoute>} />
      <Route path="/santri/profil" element={<ProtectedRoute roles={['santri']}><SantriProfile /></ProtectedRoute>} />
      <Route path="/santri/ujian-tahfidz" element={<ProtectedRoute roles={['santri']}><SantriUjianTahfidz /></ProtectedRoute>} />

      {/* Guru profile */}
      <Route path="/guru/profil" element={<ProtectedRoute roles={['guru']}><GuruProfile /></ProtectedRoute>} />

      {/* Chat - shared Guru & Santri */}
      <Route path="/chat" element={<ProtectedRoute roles={['guru', 'santri']}><ChatPage /></ProtectedRoute>} />

      {/* Chatbot - Santri */}
      <Route path="/chatbot" element={<ProtectedRoute roles={['santri']}><ChatbotPage /></ProtectedRoute>} />

      {/* Shared pages */}
      <Route path="/berita" element={<BeritaPage />} />
      <Route path="/quran" element={<AlQuranPage />} />
      <Route path="/sholat" element={<JadwalSholatPage />} />
      <Route path="/toko" element={<TokoPage />} />
      <Route path="/toko/:id" element={<TokoProdukDetail />} />
      <Route path="/donasi" element={<DonasiPage />} />
      <Route path="/tasbih" element={<TasbihPage />} />
      <Route path="/pedoman" element={<PedomanPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <PWAInstallProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </div>
        </BrowserRouter>
      </PWAInstallProvider>
    </AuthProvider>
  );
}

export default App;
